import * as moment from 'moment';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from "react-redux";
import { Collapse } from 'reactstrap';
import { bindActionCreators, Dispatch } from "redux";
import { signOut as signOutAction } from "../../actions/loginActions";
import { clearMailData as clearMailDataAction } from "../../actions/mailActions";
import { addNotification as addNotificationAction, clearNotifications as clearNotificationsAction } from "../../actions/notificationsActions";
import { saveSettings as saveSettingsAction } from "../../actions/settingsActions";
import { SettingsService } from '../../services/settings';
import Notification from '../common/Notification';
import UserSettings from './model/Settings';

interface ISettingsProps {
  csrf: string;
  isOpen: boolean;
  loginTime: number;
  settings: UserSettings;
  user: string;
  wsid: string;
  addNotification: (notification: Notification) => any;
  clearMailData: () => any;
  clearNotifications: () => any;
  saveSettings: (settings: UserSettings) => any;
  signOut: () => any;
}

interface ISettingsState {
  isOpen: boolean;
  loadError: boolean;
  settings: UserSettings;
}

export class Settings extends React.Component<ISettingsProps, ISettingsState> {
  public settingsService: SettingsService;
  private notifyValues = [
    { label: "L.Notify.Daily", value: "daily" },
    { label: "L.Notify.Weekly", value: "weekly" },
    { label: "L.Notify.Never", value: "never" }
  ];

  private dateFormatValues = [
    { label: "L.Date.MDY", value: "mm/dd/yyyy" },
    { label: "L.Date.DMY", value: "dd/mm/yyyy" }
  ];

  private languageValues = [
    { label: "English (American)", value: "en_US" },
    { label: "Deutsch (German)", value: "de_DE" },
    { label: "Ελληνικά (Greek)", value: "el_GR" },
    { label: "Español (Spanish)", value: "es_ES" },
    { label: "Français (Canadian)", value: "fr_CA" },
    { label: "Français (French)", value: "fr_FR" },
    { label: "Italiano (Italian)", value: "it_IT" },
    { label: "日本語 (Japanese)", value: "ja_JP" },
    { label: "Polski (Polish)", value: "pl_PL" },
    { label: "Português (Portuguese)", value: "pt_BR" },
    { label: "ไทย (Thai)", value: "th_TH" },
    { label: "简体中文 (Chinese)", value: "zh_CN" }
  ];

  constructor(props: ISettingsProps) {
    super(props);
    const initialSettings = this.props.settings ?
      { notify: this.props.settings.notify, dateFormat: this.props.settings.dateFormat, language: this.props.settings.language } as UserSettings
      : { notify: this.notifyValues[0].value, dateFormat: this.dateFormatValues[0].value, language: this.languageValues[0].value } as UserSettings;

    this.state = { isOpen: this.props.isOpen, loadError: false, settings: initialSettings };
    this.settingsService = new SettingsService(this.props.user, this.props.wsid, this.props.csrf);
    this.onSelectChange = this.onSelectChange.bind(this);
    this.loadSettings = this.loadSettings.bind(this);
    this.save = this.save.bind(this);
  }

  public componentDidMount() {
    if (this.props.wsid !== null && this.props.csrf !== null && this.props.user !== null) {
      this.loadSettings();
    }
  }

  public async loadSettings() {
    try {
      const response = await this.settingsService.getSettings();

      if (!response.ok) {
        throw new Error(response.status.toString());
      }

      const settings = await response.json();
      this.props.saveSettings({ dateFormat: settings.dateFormat, language: settings.language, notify: settings.notify });
    } catch (e) {
      if (e.message === '401') {
        if (moment().diff(this.props.loginTime, 'hours') < 12) {
          this.props.addNotification({ message: "Session Timeout", isError: true });
        }
        this.props.signOut();
        this.props.clearMailData();
      } else {
        this.props.addNotification({ messageKey: "L.Error.Load", isError: true });
        this.setState({ loadError: true });
      }
    }
  }

  public async save(field: string, value: string) {
    try {
      const response = await this.settingsService.save(field, value);

      if (!response.ok) {
        throw new Error(response.status.toString());
      }

      const newSettings = { ...this.state.settings };
      newSettings[field] = value;
      this.props.saveSettings(newSettings);
      this.setState({ settings: newSettings });
    } catch (e) {
      if (e.message === '401') {
        if (moment().diff(this.props.loginTime, 'hours') < 12) {
          this.props.addNotification({ message: "Session Timeout", isError: true });
        }
        this.props.signOut();
        this.props.clearMailData();
      } else {
        this.props.addNotification({ messageKey: "L.Error.Save", isError: true });
      }
    }
  }

  public onSelectChange = (e: React.FormEvent<HTMLSelectElement>) => {
    if (e.currentTarget.dataset.type) {
      this.save(e.currentTarget.dataset.type, e.currentTarget.value);
    }
  }

  public componentWillReceiveProps(nextProps: ISettingsProps) {
    if (this.props.isOpen !== nextProps.isOpen) {
      const storedSettings = nextProps.settings;
      const settings = storedSettings ?
        { ...storedSettings } as UserSettings
        : { notify: this.notifyValues[0].value, dateFormat: this.dateFormatValues[0].value, language: this.languageValues[0].value } as UserSettings;

      this.setState({ isOpen: nextProps.isOpen, settings });
    }
  }

  public render() {
    const notifyOptions = this.notifyValues.map((notifyOption) => (
      <option value={notifyOption.value} key={notifyOption.label}>
        <FormattedMessage id={notifyOption.label} defaultMessage={notifyOption.value} />
      </option>)
    );
    const dateFormatOptions = this.dateFormatValues.map((dateFormatOption) => (
      <option value={dateFormatOption.value} key={dateFormatOption.label}>
        <FormattedMessage id={dateFormatOption.label} defaultMessage={dateFormatOption.value} />
      </option>)
    );
    const languageOptions = this.languageValues.map((v) => (<option value={v.value} key={v.value}>{v.label}</option>));

    return (
      <Collapse isOpen={this.state.isOpen}>
        <div className="p-4 settings">
          <h4>
            <FormattedMessage id="L.Settings" defaultMessage="Settings" />
          </h4>
          <div className="row align-items-center">
            <div className="col-xl-1 col-lg-2 col-md-2 col-sm-4">
              <label>
                <FormattedMessage id="L.Notify" defaultMessage="Notify" />
              </label>
            </div>
            <div className="col-xl-2 col-lg-6 col-md-5 col-sm-8">
              <select className="custom-select" id="notify" data-type="notify" disabled={this.state.loadError} value={this.state.settings.notify} onChange={this.onSelectChange}>
                {notifyOptions}
              </select>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-xl-1 col-lg-2 col-md-2 col-sm-4">
              <label>
                <FormattedMessage id="L.Date" defaultMessage="Date Format" />
              </label>
            </div>
            <div className="col-xl-2 col-lg-6 col-md-5 col-sm-8">
              <select className="custom-select" id="dateFormat" data-type="dateFormat" disabled={this.state.loadError} value={this.state.settings.dateFormat} onChange={this.onSelectChange}>
                {dateFormatOptions}
              </select>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-xl-1 col-lg-2 col-md-2 col-sm-4">
              <label>
                <FormattedMessage id="L.Language" defaultMessage="Language" />
              </label>
            </div>
            <div className="col-xl-2 col-lg-6 col-md-5 col-sm-8">
              <select className="custom-select" id="language" data-type="language" disabled={this.state.loadError} value={this.state.settings.language} onChange={this.onSelectChange}>
                {languageOptions}
              </select>
            </div>
          </div>
        </div>
      </Collapse>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    csrf: state.authentication.csrf,
    loginTime: state.authentication.loginTime,
    settings: state.settings.settings,
    user: state.authentication.user,
    wsid: state.authentication.wsid,
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    addNotification: bindActionCreators(addNotificationAction, dispatch),
    clearMailData: bindActionCreators(clearMailDataAction, dispatch),
    clearNotifications: bindActionCreators(clearNotificationsAction, dispatch),
    saveSettings: bindActionCreators(saveSettingsAction, dispatch),
    signOut: bindActionCreators(signOutAction, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Settings);



// WEBPACK FOOTER //
// ./src/components/settings/Settings.tsx